import { registerSW } from 'virtual:pwa-register';

function isServiceWorkerSupported(): boolean {
  return import.meta.env.PROD && 'serviceWorker' in navigator;
}

function hasPendingUpdate(registration: ServiceWorkerRegistration): boolean {
  return (
    registration.waiting !== null && navigator.serviceWorker.controller !== null
  );
}

type BootstrapPwaOptions = {
  onUpdateAvailable: (applyUpdate: () => Promise<void>) => void;
  onApplyingUpdate: () => void;
};

export function bootstrapPwaUpdates(options: BootstrapPwaOptions): () => void {
  if (!isServiceWorkerSupported()) {
    return () => {};
  }

  let updateCheckInterval: number | null = null;
  let isApplyingUpdate = false;

  const applyUpdate = async () => {
    if (isApplyingUpdate) {
      return;
    }
    isApplyingUpdate = true;
    options.onApplyingUpdate();
    await updateSW(true);
  };

  const notifyIfUpdateAvailable = () => {
    options.onUpdateAvailable(applyUpdate);
  };

  const checkRegistration = (registration: ServiceWorkerRegistration) => {
    if (hasPendingUpdate(registration)) {
      notifyIfUpdateAvailable();
    }
    void registration.update();
  };

  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      notifyIfUpdateAvailable();
    },
    onRegisteredSW(_swScriptUrl, registration) {
      if (registration === undefined) {
        return;
      }

      checkRegistration(registration);

      const intervalMs = 60_000;
      updateCheckInterval = window.setInterval(() => {
        void registration.update();
      }, intervalMs);
    },
  });

  void navigator.serviceWorker.getRegistration().then((registration) => {
    if (registration === undefined) {
      return;
    }
    checkRegistration(registration);
  });

  return () => {
    if (updateCheckInterval !== null) {
      window.clearInterval(updateCheckInterval);
    }
  };
}
